import { RefObject, useEffect, useRef } from 'react';
import { usersApi } from 'apis/CompanyAPI/users/usersApi';
import { CompanyImpressionSource, companyUpdateAPI } from 'apis/CompanyAPI/content/UpdateAPI';
import { createImpressionQueue } from 'domain/Updates/shared/impressionQueue';
import { isPostOnScreen } from 'domain/Updates/shared/isPostOnScreen';

const FLUSH_INTERVAL_MS = 5000;
// A post counts as seen when it has been on screen for a full second
const VISIBLE_FOR_MS = 1000;
// Several steps, so a post taller than the viewport still reports when it comes to cover half of it
const OBSERVED_THRESHOLDS = [0, 0.1, 0.25, 0.5, 0.75, 1];

/** Where the post was on screen: the feed, or a company's updates tab or company card */
export type ImpressionTarget =
  { type: 'feed' } | { type: 'company'; companyId: number; source: CompanyImpressionSource };

const targetKey = (target: ImpressionTarget) =>
  target.type === 'feed' ? 'feed' : `company:${target.companyId}:${target.source}`;

// One queue per target, since feed and company impressions go to different endpoints
const queues = new Map<string, ReturnType<typeof createImpressionQueue>>();
let flushTimer: ReturnType<typeof setTimeout> | undefined;
let listeningForPageHide = false;

function queueFor(target: ImpressionTarget) {
  const key = targetKey(target);
  let queue = queues.get(key);
  if (!queue) {
    queue = createImpressionQueue(ids =>
      target.type === 'feed'
        ? usersApi.logFeedImpressions(ids)
        : companyUpdateAPI(target.companyId).logImpressions(ids, target.source),
    );
    queues.set(key, queue);
  }
  return queue;
}

function flush() {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = undefined;
  queues.forEach(queue => queue.flush());
}

function enqueue(contentId: string, target: ImpressionTarget) {
  const queue = queueFor(target);
  queue.add(contentId);
  if (!listeningForPageHide) {
    listeningForPageHide = true;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush();
    });
  }
  if (queue.hasPending() && !flushTimer) {
    flushTimer = setTimeout(flush, FLUSH_INTERVAL_MS);
  }
}

/**
 * Logs an impression for `contentId` once the element in `ref` has been on screen (see `isPostOnScreen`) for 1 second.
 * Pass an undefined `contentId` to skip logging, e.g. for the company's own admins.
 *
 * Decides inside the observer callback, on `isIntersecting`, rather than through `useIsElementVisible`: that hook
 * re-checks `intersectionRatio >= threshold`, and a crossing reported as 0.4997 left the post "not visible" for good.
 */
export default function usePostImpression(
  contentId: string | undefined,
  ref: RefObject<HTMLElement>,
  target: ImpressionTarget,
) {
  // Callers pass an object literal, so key the effect on the target's value and read the live object through a ref
  const key = targetKey(target);
  const targetRef = useRef(target);
  targetRef.current = target;

  useEffect(() => {
    const element = ref.current;
    if (!element || !contentId) return;

    let dwellTimer: ReturnType<typeof setTimeout> | undefined;
    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[entries.length - 1];
        if (isPostOnScreen(entry, window.innerHeight)) {
          if (!dwellTimer) dwellTimer = setTimeout(() => enqueue(contentId, targetRef.current), VISIBLE_FOR_MS);
        } else if (dwellTimer) {
          clearTimeout(dwellTimer);
          dwellTimer = undefined;
        }
      },
      { threshold: OBSERVED_THRESHOLDS },
    );
    observer.observe(element);

    return () => {
      if (dwellTimer) clearTimeout(dwellTimer);
      observer.disconnect();
    };
  }, [ref, contentId, key]);
}
